/**
 * PbrNotice — the full PBR logo + verbatim warning text block.
 *
 * Render this on every view that also renders a `<PbrMark>` for a
 * protected variety. The IP Australia industry guidelines require the
 * symbol and the accompanying warning to appear together — neither is
 * sufficient on its own.
 *
 * Two render modes:
 *
 *   - mode="granted" (default): the standard warning paragraph for
 *     varieties under provisional or full PBR protection. Uses the
 *     verbatim `PBR_WARNING_TEXT` constant.
 *   - mode="testMarketing": the Section 43(6) pre-application notice for
 *     plants sold during the test-marketing window before a PBR
 *     application is lodged. Requires an `expiryDate` (YYYY-MM-DD).
 *
 * Artwork: official IP Australia "PBR logo" PNG (droplet + "PBR" wordmark),
 * two variants (black / PMS 562 colour). Selected via the `color` prop.
 *   - Italicise the Act name per the published artwork.
 *   - Renders nothing if a cultivar is passed and it is not under PBR.
 */
import type { Cultivar } from '../../types/cultivar';
import {
  PBR_ACT_NAME,
  PBR_TEST_MARKETING_TEMPLATE,
  PBR_WARNING_TEXT,
  isPbrProtected,
  type PbrColor,
} from '../../constants/pbr';
import { cn } from '../../utils/cn';
import pbrLogoBlack from '../../assets/pbr/pbr-logo-black.png';
import pbrLogoColour from '../../assets/pbr/pbr-logo-colour.png';

type Props = {
  /** Cultivar to gate visibility on — render nothing if not protected. */
  cultivar?: Pick<Cultivar, 'protection_status'> | null;
  /** Mode: granted/provisional warning (default), or pre-application test-marketing notice. */
  mode?: 'granted' | 'testMarketing';
  /** Required when mode='testMarketing' — ISO date string (YYYY-MM-DD) for the eligibility expiry. */
  expiryDate?: string;
  /** Colour variant for the logo. */
  color?: PbrColor;
  /** Optional className for the outer block. */
  className?: string;
  /** If true, render without the surrounding card chrome (for embedding in a parent panel). */
  bare?: boolean;
};

export default function PbrNotice({
  cultivar,
  mode = 'granted',
  expiryDate,
  color = 'black',
  className,
  bare = false,
}: Props) {
  // If a cultivar is passed, gate visibility on its protection status.
  if (cultivar && !isPbrProtected(cultivar.protection_status)) {
    return null;
  }

  const logoSrc = color === 'pms562' ? pbrLogoColour : pbrLogoBlack;

  // Compose the warning content. Split on the Act name so we can italicise it
  // without splitting the verbatim string at the constants layer.
  const baseText =
    mode === 'testMarketing'
      ? PBR_TEST_MARKETING_TEMPLATE.replace(
          '{date}',
          expiryDate && expiryDate.trim() ? expiryDate : '[date]',
        )
      : PBR_WARNING_TEXT;

  const [beforeAct, afterAct] = baseText.split(PBR_ACT_NAME);

  return (
    <aside
      role="note"
      aria-label="Plant Breeder’s Rights notice"
      className={cn(
        !bare && 'rounded-md border border-neutral-200 bg-neutral-50 p-4',
        'flex items-start gap-3 text-xs leading-snug text-neutral-700',
        className,
      )}
    >
      <img
        src={logoSrc}
        alt="PBR logo"
        style={{ width: 48, height: 'auto', flexShrink: 0 }}
      />

      <p className="m-0">
        {beforeAct}
        <em>{PBR_ACT_NAME}</em>
        {afterAct}
      </p>
    </aside>
  );
}
